// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React from 'react'
import { createPortal } from 'react-dom'
import './style.less'
import { getViewportSize, getScrollTop, getScrollLeft, domContains } from 'utils/dom'

export enum Alignment {
  Top,
  Bottom,
  Left,
  Right
}

export interface IPopupProps {
  anchor: {
    x: number
    y: number
    height?: number
    width?: number
  }
  offsetX?: number
  offsetY?: number
  horizentalAlignment?: Alignment
  vertivalAlignment?: Alignment
  visible: boolean
  onDissmiss: () => void
}

export interface IPopupState {
  top: number
  left: number
}

enum LayoutPosition {
  Before,
  After
}

export class Popup extends React.Component<IPopupProps, IPopupState> {
  state = {
    top: 0,
    left: 0
  }

  static defaultProps = {
    anchor: {
      x: 0,
      y: 0,
      width: 0,
      height: 0
    },
    offsetX: 0,
    offsetY: 0,
    horizentalAlignment: Alignment.Left,
    vertivalAlignment: Alignment.Bottom,
    visible: false,
    onDissmiss: () => {}
  }

  private popupRef = React.createRef<HTMLDivElement>()

  componentDidMount () {
    document.body.addEventListener('mousedown', this.handleClickOutside)
    const { left, top } = this.layout(this.props)
    this.setState({
      left,
      top
    })
  }

  componentDidUpdate (prevProps) {
    if (this.isPropsChanged(prevProps, this.props)) {
      const { left, top } = this.layout(this.props)
      this.setState({
        left,
        top
      })
    }
  }

  componentWillUnmount () {
    document.body.removeEventListener('mousedown', this.handleClickOutside)
  }

  private handleClickOutside = (e: MouseEvent) => {
    const { onDissmiss } = this.props
    if (!domContains(this.popupRef.current, e.target as Node)) {
      onDissmiss()
    }
  }

  private isXYInsideAnchor = (x: number, y: number): boolean => {
    const { anchor } = this.props
    if (anchor.width <= 0 || anchor.height <= 0) {
      return false
    }
    return x >= anchor.x && x <= anchor.x + anchor.width && y >= anchor.y && y <= anchor.y + anchor.height
  }

  private isPropsChanged (oldProps, newProps): boolean {
    let dirty = false
    const keys = ['offsetX', 'offsetY', 'horizentalAlignment', 'vertivalAlignment', 'visible']
    keys.forEach(key => {
      if (oldProps[key] !== newProps[key]) {
        dirty = true
        return
      }
    })
    for (let k in oldProps.anchor) {
      if (oldProps.anchor[k] !== newProps.anchor[k]) {
        dirty = true
      }
    }
    return dirty
  }

  private layout (props: IPopupProps): { top: number; left: number } {
    const { anchor, offsetX, offsetY, horizentalAlignment, vertivalAlignment } = props
    let top,
      left = null
    const viewport = getViewportSize()
    const popup = this.popupRef.current
    const popupWidth = popup.offsetWidth
    const popupHeight = popup.offsetHeight
    let layoutPosition: LayoutPosition
    if (vertivalAlignment === Alignment.Top) {
      top = anchor.y + getScrollTop() - offsetY
      layoutPosition = LayoutPosition.After
    } else if (vertivalAlignment === Alignment.Bottom) {
      top = anchor.y + getScrollTop() + offsetY
      layoutPosition = LayoutPosition.Before
    }
    top = layout(viewport.height, popupHeight, top, anchor.height, layoutPosition)

    if (horizentalAlignment === Alignment.Left) {
      left = anchor.x + getScrollLeft() - offsetX
      layoutPosition = LayoutPosition.Before
    } else if (horizentalAlignment === Alignment.Right) {
      left = anchor.x + anchor.width + getScrollLeft() + offsetX
      layoutPosition = LayoutPosition.After
    }
    left = layout(viewport.width, popupWidth, left, 0, layoutPosition)
    return {
      top,
      left
    }
  }

  render () {
    const { children, visible } = this.props
    const { top, left } = this.state
    const style = {
      top,
      left,
      display: visible ? 'block' : 'none'
    }
    return createPortal(
      <div className='popup' style={style} ref={this.popupRef}>
        <div className='popup-inner'>{children}</div>
      </div>,
      document.body
    )
  }
}

function layout (
  viewportSize: number,
  popupSize: number,
  anchorOffset: number,
  anchorSize: number,
  layoutPosition: LayoutPosition
): number {
  let anchorEnd = anchorOffset + anchorSize
  if (layoutPosition === LayoutPosition.Before) {
    if (popupSize <= viewportSize - anchorEnd) {
      return anchorEnd
    }
    if (popupSize <= anchorOffset) {
      return anchorOffset - popupSize
    }
    return Math.max(viewportSize - popupSize, 0)
  } else {
    // display before the anchor
    if (popupSize <= anchorOffset) {
      return anchorOffset - popupSize
    }
    if (popupSize <= viewportSize - anchorEnd) {
      return anchorEnd
    }
    return 0
  }
}
